import React, { useState } from 'react';
import { Container, Title, Text, Stack, TextInput, Textarea, Button, Group, Alert, Box } from '@mantine/core';
import { IconMail, IconPhone, IconMapPin, IconSend } from '@tabler/icons-react';
import { useLanguage } from '@/context/LanguageContext';
import { useSEO } from '@/lib/useSEO';
import { api } from '@/lib/api';

export default function ContactPage() {
  const { t } = useLanguage();

  // SEO for Contact page
  useSEO({
    title: t('seo.contact.title') || 'Contact Us - Luxelo',
    description: t('seo.contact.description') || 'Get in touch with Luxelo. We are here to help with any questions about our LED lighting products.',
    type: 'website',
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setError(t('contact.form.error.required'));
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('contact.form.error.invalidEmail'));
      setLoading(false);
      return;
    }

    try {
      await api.contact.send(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      setError(err.message || t('contact.form.error.sendFailed') || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Box>
          <Title order={1} mb="md">{t('contact.title')}</Title>
          <Text size="lg" c="dimmed">
            {t('contact.subtitle')}
          </Text>
        </Box>

        <Group align="flex-start" gap="xl">
          <Box style={{ flex: 1 }}>
            <Title order={2} mb="md">{t('contact.form.title')}</Title>
            
            {submitted && (
              <Alert color="green" mb="md">
                {t('contact.form.success')}
              </Alert>
            )}

            {error && (
              <Alert color="red" mb="md">
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <TextInput
                  label={t('contact.form.name')}
                  placeholder={t('contact.form.namePlaceholder')}
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <TextInput
                  label={t('contact.form.email')}
                  placeholder={t('contact.form.emailPlaceholder')}
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
                <TextInput
                  label={t('contact.form.subject')}
                  placeholder={t('contact.form.subjectPlaceholder')}
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                />
                <Textarea
                  label={t('contact.form.message')}
                  placeholder={t('contact.form.messagePlaceholder')}
                  required
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
                <Button type="submit" leftSection={<IconSend size={16} />} size="md" loading={loading}>
                  {t('contact.form.submit')}
                </Button>
              </Stack>
            </form>
          </Box>

          <Box style={{ flex: 1 }}>
            <Title order={2} mb="md">{t('contact.info.title')}</Title>
            <Stack gap="lg">
              <Group gap="md" align="flex-start">
                <IconMail size={24} style={{ color: '#228be6', marginTop: '4px', flexShrink: 0 }} />
                <Box>
                  <Text fw={600} mb="xs">{t('contact.info.email')}</Text>
                  <Text size="sm" c="dimmed">contact@luxelo.de</Text>
                </Box>
              </Group>
              <Group gap="md" align="flex-start">
                <IconPhone size={24} style={{ color: '#228be6', marginTop: '4px', flexShrink: 0 }} />
                <Box>
                  <Text fw={600} mb="xs">{t('contact.info.phone')}</Text>
                  <Text size="sm" c="dimmed">+48 123 456 789</Text>
                </Box>
              </Group>
              <Group gap="md" align="flex-start">
                <IconMapPin size={24} style={{ color: '#228be6', marginTop: '4px', flexShrink: 0 }} />
                <Box>
                  <Text fw={600} mb="xs">{t('contact.info.address')}</Text>
                  <Text size="sm" c="dimmed">Warsaw, Poland</Text>
                </Box>
              </Group>
              <Box pt="md">
                <Text size="sm" c="dimmed" mb="xs">
                  {t('contact.info.hours')}
                </Text>
                <Text size="sm" c="dimmed">Monday - Friday: 9:00 AM - 6:00 PM</Text>
                <Text size="sm" c="dimmed">Saturday: 10:00 AM - 4:00 PM</Text>
                <Text size="sm" c="dimmed">Sunday: Closed</Text>
              </Box>
            </Stack>
          </Box>
        </Group>
      </Stack>
    </Container>
  );
}

